import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import type { AuthHandle } from "./client";
import { ApiClient } from "./client";
import { useApprovals } from "../governance/approval-context";
import { appConfig } from "../config";

// Provides the single ApiClient to the tree, wired with the auth handle (bearer +
// refresh + step-up) and the four-eyes approval gateway from the governance
// context. One client, one place governance lives.
const ApiContext = createContext<ApiClient | null>(null);

export interface ApiProviderProps {
  auth: AuthHandle;
  children: ReactNode;
  /** Test seam: inject a prebuilt client (bypasses config/approvals wiring). */
  client?: ApiClient;
}

export function ApiProvider({ auth, children, client }: ApiProviderProps) {
  const approvals = useApprovals();
  const value = useMemo(
    () => client ?? new ApiClient({ baseUrl: appConfig.apiBaseUrl, auth, approvals }),
    [client, auth, approvals],
  );
  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

export function useApi(): ApiClient {
  const ctx = useContext(ApiContext);
  if (!ctx) throw new Error("useApi must be used within an ApiProvider");
  return ctx;
}
